import { BlockedButton } from "components/BlockedButton/BlockedButton";
import { Button } from "components/DesignSystem/Button/Button";
import Modal from "components/DesignSystem/Modal/Modal";
import { Tag } from "components/DesignSystem/Tag/Tag";
import { ArrowRight } from "components/icons/ArrowRight";
import { PermissionBasedUI } from "components/PermissionBasedUI/PermissionBasedUI";
import { PlaidToDirectMigration } from "components/PlaidToDirectMigration/PlaidToDirectMigration";
import {
  CONNECT_BANK_ACCOUNT,
  CONNECT_REVENUE_SOURCE,
} from "constants/subscriptionPermissionFeatures";
import { onConnectArgs } from "hooks/useBankConnect";
import { useCurrentEntityId } from "hooks/useCurrentEntityId";
import { useCurrentGroupContext } from "hooks/useCurrentGroupContext";
import { useModal } from "hooks/useModal";
import { useRoleBasedView } from "hooks/useRoleBasedView";
import { AccountCard } from "pages/Books/DataSources/DataSourcesList";
import { ComponentProps, useState } from "react";
import PlaidMultipleBankLogoCircle from "static/images/PlaidMultipleBankLogoCircle.svg";
import GustoIcon from "static/images/GustoIcon.svg";
import {
  Connection,
  useGetAllConnectionsQuery,
} from "store/apis/booksConnections";
import { OtherBankingInstitution } from "./OtherBankingInstitution";
import Async from "components/DesignSystem/AsyncComponents/Async";
import { EmptyScreen } from "pages/Books/EmptyScreen";
import { EntitySelector } from "components/EntitySelector/EntitySelector";
import { Banner } from "components/DesignSystem/Banner/Banner";
import { useGetBookkeepingSettingsQuery } from "store/apis/booksLockDate";
import dayjs from "dayjs";
import { ConditionalLink } from "components/conditionalLink";
import { useConstructInternalLink } from "hooks/useConstructInternalLink";
import { useCurrentEntity } from "hooks/useCurrentEntity";
import { DiamondPurple } from "components/icons/DiamondPurple";
import { PermissionBasedView } from "components/PermissionBasedView/PermissionBasedView";
import { BooksSoftwareSubscriptionsModal } from "components/BooksSoftwareSubscriptionsModal";
import { BooksSubscriptionOnboardingModal } from "components/BooksSubscriptionOnboardingModal";
import { ModalDescriptionWrapper } from "pages/Books/RevenueMetrics/RevenueMetricsLocked";
import { ConnectBankAccount } from "components/ConnectBankAccount/ConnectBankAccount";
import Tab from "components/DesignSystem/Tab/Tab";
import { GustoAccess } from "components/GustoAccess/GustoAccess";
import ToolTip from "components/design/toolTip";
import classNames from "classnames";
import {
  ACCOUNT_CONNECTION_TYPE,
  AccountConnectionType,
} from "constants/dataSource";
import BillCom from "static/images/BillCom.svg";
import { ConnectBillDotCom } from "components/BillComLink.tsx/ConnectBillDotCom";

export type ConnectionsModalProps = {
  onConnect: (args: onConnectArgs) => void;
  entityIdFromParent?: string;
  showEntityLabel?: boolean;
  defaultTab?: AccountConnectionType;
  disableNewConnections?: boolean;
  disabledTooltip?: string;
  isOnboarding?: boolean;
};

export type BankingConnectionsProps = {
  connection: Connection;
  entityId: string;
  onComplete?: () => void;
};

export const RevenueConnections = ({
  connection,
  onConnect,
  entityId,
  isOnboarding,
}: {
  connection: Connection;
  entityId: string;
  isOnboarding?: boolean;
} & Pick<ComponentProps<typeof ConnectionsModal>, "onConnect">) => {
  const { isCpa } = useRoleBasedView();
  const { uuid: groupId } = useCurrentGroupContext();
  const openSubscriptionModal = useModal();

  return (
    <>
      <AccountCard
        key={connection.uuid}
        logo={
          <img
            className="t-w-8 t-h-8 t-rounded-full"
            src={connection.logo_url}
            alt=""
          />
        }
        title={
          <div className="t-flex t-flex-col t-gap-1.5">
            <div>{connection.name}</div>
            <div className="t-text-purple t-text-body-sm t-flex t-gap-1 t-items-center">
              Books Standard Plan Exclusive
            </div>
          </div>
        }
        description="Direct API integration for fetching revenue data"
        CTA={
          <PermissionBasedView
            errorMessage="You need to upgrade to Plus Plan to connect revenue data sources."
            feature={CONNECT_REVENUE_SOURCE}
            blockedUI={
              <Button onClick={() => openSubscriptionModal.open()} size="small">
                <div className="t-flex t-items-center t-gap-1.5">
                  <DiamondPurple />
                  Connect
                  <div className="t-flex t-items-center t-justify-center -t-ml-1">
                    <ArrowRight color="currentColor" />
                  </div>
                </div>
              </Button>
            }
            entityId={entityId}
          >
            <Button
              size="small"
              onClick={() =>
                onConnect({
                  connectionId: connection.uuid,
                  entityIdInArg: entityId,
                  invokedFrom: isCpa
                    ? `/books/data-sources?company=${groupId}&entity=${entityId}`
                    : `/books/data-sources?entity=${entityId}`,
                })
              }
            >
              Connect <ArrowRight color="currentColor" />
            </Button>
          </PermissionBasedView>
        }
      />
      {isOnboarding ? (
        <BooksSubscriptionOnboardingModal
          open={openSubscriptionModal.isOpen}
          onClose={openSubscriptionModal.close}
          entityId={entityId}
          onSubscribed={openSubscriptionModal.close}
        />
      ) : (
        <BooksSoftwareSubscriptionsModal
          open={openSubscriptionModal.isOpen}
          onClose={openSubscriptionModal.close}
          entityId={entityId}
          onProductSubscribe={openSubscriptionModal.close}
          modalDescription={
            <ModalDescriptionWrapper value="Unlock Stripe integration and more with Books Standard Plan" />
          }
        />
      )}
    </>
  );
};

export const BankingConnections = ({
  connection,
  entityId,
  onComplete,
  disableNewConnections,
  disabledTooltip,
  isOnboarding,
}: BankingConnectionsProps &
  Pick<
    ComponentProps<typeof ConnectionsModal>,
    "onConnect" | "disableNewConnections" | "disabledTooltip" | "isOnboarding"
  >) => {
  const subscriptionModal = useModal();
  const connectModal = useModal();

  return (
    <>
      <AccountCard
        key={connection.uuid}
        logo={
          <img
            className="t-w-8 t-h-8 t-rounded-full"
            src={connection.logo_url}
            alt=""
          />
        }
        title={connection.name}
        description="Direct API integration for fetching transactions data"
        CTA={
          <>
            {!connection.is_connection_available ? (
              <div className="t-flex t-flex-col t-items-end t-gap-1">
                {disableNewConnections ? (
                  <ToolTip text={disabledTooltip || ""}>
                    <div>
                      <Button size="small" disabled>
                        Connect <ArrowRight color="currentColor" />
                      </Button>
                    </div>
                  </ToolTip>
                ) : (
                  <PermissionBasedView
                    errorMessage="You need to upgrade to Plus Plan to connect revenue data sources."
                    feature={CONNECT_BANK_ACCOUNT}
                    blockedUI={
                      <Button
                        onClick={() => subscriptionModal.open()}
                        size="small"
                      >
                        <div className="t-flex t-items-center t-gap-1.5">
                          <DiamondPurple />
                          Connect
                          <div className="t-flex t-items-center t-justify-center -t-ml-1">
                            <ArrowRight color="currentColor" />
                          </div>
                        </div>
                      </Button>
                    }
                    entityId={entityId}
                  >
                    <Button size="small" onClick={connectModal.open}>
                      Connect
                      <ArrowRight color="currentColor" />
                    </Button>
                    <ConnectBankAccount
                      onSuccess={onComplete}
                      entityId={entityId}
                      bank={{
                        name: connection.name,
                        logo: connection.logo_url,
                        connection_id: connection.uuid,
                      }}
                      {...connectModal}
                    />
                  </PermissionBasedView>
                )}
              </div>
            ) : (
              <Tag tagType="green">
                <span className="t-text-body-sm">Connected</span>
              </Tag>
            )}
          </>
        }
      />
      {isOnboarding ? (
        <BooksSubscriptionOnboardingModal
          open={subscriptionModal.isOpen}
          onClose={subscriptionModal.close}
          entityId={entityId}
          onSubscribed={subscriptionModal.close}
        />
      ) : (
        <BooksSoftwareSubscriptionsModal
          open={subscriptionModal.isOpen}
          onClose={subscriptionModal.close}
          entityId={entityId}
          onProductSubscribe={subscriptionModal.close}
          modalDescription={
            <ModalDescriptionWrapper
              value={
                "Unlock the ability to connect more than 2 bank accounts and access Stripe integration with the Books Standard Plan"
              }
            />
          }
        />
      )}
    </>
  );
};

export const ConnectionsModal = ({
  onConnect,
  entityIdFromParent,
  showEntityLabel = true,
  onComplete,
  onManualBankAddSuccess,
  defaultTab,
  disableNewConnections,
  disabledTooltip,
  isOnboarding,
}: ConnectionsModalProps &
  Pick<
    ComponentProps<typeof OtherBankingInstitution>,
    "onManualBankAddSuccess" | "onComplete"
  >) => {
  const { uuid: groupId, entities } = useCurrentGroupContext();
  const entity = useCurrentEntity();
  const entityIdFromQuery = useCurrentEntityId();
  const entityId = entityIdFromQuery || entityIdFromParent!;
  const { link } = useConstructInternalLink();

  const {
    data: connections,
    isLoading,
    isSuccess,
  } = useGetAllConnectionsQuery(
    {
      groupId: groupId!,
      entityId: entityId!,
    },
    { skip: !groupId || !entityId }
  );

  const { data: bookkeepingSettings } = useGetBookkeepingSettingsQuery(
    {
      entityId: entityId!,
    },
    { skip: !groupId || !entityId }
  );

  const [currentConnectionId, setCurrentConnectionId] = useState<string | null>(
    null
  );
  const plaidToDirectMigrationStrategyModal = useModal();
  const otherBankingInstitutionModal = useModal();

  const onPlaidToDirectMigration = (connectionId: string) => {
    plaidToDirectMigrationStrategyModal.open();
    setCurrentConnectionId(connectionId);
  };

  const revenueConnections = connections?.filter(
    ({ connection_provider }) => connection_provider === "STRIPE"
  );
  const bankingConnections = connections?.filter(
    ({ connection_provider }) =>
      connection_provider !== "STRIPE" &&
      connection_provider !== "BILL.COM" &&
      connection_provider !== "HUBSPOT"
  );
  const billComConnections = connections?.filter(
    ({ connection_provider }) => connection_provider === "BILL.COM"
  );
  const hubspotConnections = connections?.filter(
    ({ connection_provider }) => connection_provider === "HUBSPOT"
  );

  return (
    <>
      <Async.Root
        isSuccess={isSuccess}
        isLoading={isLoading}
        isEmpty={entities?.length === 0}
      >
        <Async.Empty>
          <EmptyScreen
            text={
              <div className="t-text-text-30 t-flex t-flex-col">
                <span className="t-text-subtext">No data sources found</span>
              </div>
            }
          ></EmptyScreen>
        </Async.Empty>
        <Async.Success>
          <div
            className={classNames("t-relative", {
              "t-flex t-flex-col": showEntityLabel,
            })}
          >
            <Tab.Root
              defaultValue={
                defaultTab || ACCOUNT_CONNECTION_TYPE.BANK_AND_CREDIT_CARD
              }
              className="t-h-full t-flex t-flex-col t-gap-3"
            >
              <div
                className={classNames(
                  "t-sticky t-top-0 t-bg-white t-z-header sm:-t-top-5",
                  {
                    "t-flex t-flex-col t-gap-4": showEntityLabel,
                  }
                )}
              >
                {showEntityLabel ? (
                  <div className="t-flex t-items-center t-gap-2 t-text-text-30 sm:t-flex-col sm:t-items-start sm:t-gap-1.5 sm:t-text-body-sm">
                    Connecting for:
                    {entities && (
                      <EntitySelector
                        showOnlyUSEntities={true}
                        disableNonUsEntities={true}
                        showGoToEntity={true}
                        toolTipPlacement="right"
                        left={0}
                      />
                    )}
                  </div>
                ) : null}

                <Tab.List className="sm:t-gap-4">
                  <Tab.Trigger
                    value={ACCOUNT_CONNECTION_TYPE.BANK_AND_CREDIT_CARD}
                  >
                    Bank & Credit Cards
                  </Tab.Trigger>
                  <Tab.Trigger value={ACCOUNT_CONNECTION_TYPE.REVENUE}>
                    Revenue
                  </Tab.Trigger>
                  <Tab.Trigger value={ACCOUNT_CONNECTION_TYPE.PAYROLL}>
                    Payroll
                  </Tab.Trigger>
                  <Tab.Trigger value={ACCOUNT_CONNECTION_TYPE.AP_AR}>
                    AP & AR
                  </Tab.Trigger>
                </Tab.List>
              </div>

              <Tab.Content
                className="t-overflow-y-auto t-h-[28rem] sm:t-h-auto sm:t-overflow-visible t-flex t-flex-col t-gap-3"
                value={ACCOUNT_CONNECTION_TYPE.BANK_AND_CREDIT_CARD}
              >
                {bankingConnections?.map((connection) => (
                  <BankingConnections
                    key={connection.uuid}
                    connection={connection}
                    onConnect={onConnect}
                    entityId={entityId}
                    onComplete={onComplete}
                    disableNewConnections={disableNewConnections}
                    disabledTooltip={disabledTooltip}
                    isOnboarding={isOnboarding}
                  />
                ))}
                <AccountCard
                  logo={
                    <img
                      src={PlaidMultipleBankLogoCircle}
                      className="t-rounded-full t-w-6 t-h-6"
                      alt=""
                    />
                  }
                  title="Other banking institutions"
                  description="Connect other banks securely via Plaid or add manually"
                  CTA={
                    groupId &&
                    (disableNewConnections ? (
                      <ToolTip text={disabledTooltip || ""}>
                        <div>
                          <Button size="small" disabled>
                            Search <ArrowRight color="currentColor" />
                          </Button>
                        </div>
                      </ToolTip>
                    ) : (
                      <PermissionBasedUI
                        errorMessage="You need to upgrade to Plus Plan to connect data sources."
                        feature={CONNECT_BANK_ACCOUNT}
                        blockedUI={
                          <BlockedButton size="small">Search</BlockedButton>
                        }
                        entityId={entityId}
                      >
                        <Modal.Root
                          open={otherBankingInstitutionModal.isOpen}
                          onOpenChange={otherBankingInstitutionModal.toggle}
                        >
                          <Modal.Trigger asChild>
                            <Button size="small">
                              Search <ArrowRight color="currentColor" />
                            </Button>
                          </Modal.Trigger>
                          <Modal.Content size="large" useCustomOverlay>
                            <Modal.Header>
                              <Modal.Title>
                                Other Banking Institution
                              </Modal.Title>
                              <Modal.Close tabIndex={-1} />
                            </Modal.Header>
                            <OtherBankingInstitution
                              entityId={entityId}
                              onConnect={onConnect}
                              onComplete={onComplete}
                              onManualBankAddSuccess={(args) => {
                                onManualBankAddSuccess &&
                                  onManualBankAddSuccess(args);
                                otherBankingInstitutionModal.close();
                              }}
                            />
                          </Modal.Content>
                        </Modal.Root>
                      </PermissionBasedUI>
                    ))
                  }
                />
              </Tab.Content>
              <Tab.Content
                value={ACCOUNT_CONNECTION_TYPE.REVENUE}
                className="t-overflow-y-auto t-h-[28rem] sm:t-h-auto sm:t-overflow-visible"
              >
                {revenueConnections?.map((connection) => (
                  <RevenueConnections
                    key={connection.uuid}
                    connection={connection}
                    onConnect={onConnect}
                    entityId={entityId}
                    isOnboarding={isOnboarding}
                  />
                ))}
              </Tab.Content>
              <Tab.Content
                value={ACCOUNT_CONNECTION_TYPE.PAYROLL}
                className="t-overflow-y-auto t-h-[28rem] sm:t-h-auto sm:t-overflow-visible"
              >
                <AccountCard
                  logo={
                    <img
                      src={GustoIcon}
                      className="t-rounded-full t-w-6 t-h-6"
                      alt=""
                    />
                  }
                  title="Gusto"
                  description="Manually connect to allow our team to access payroll reports"
                  CTA={
                    bookkeepingSettings?.gusto_access ? (
                      <Tag tagType="green">
                        <span className="t-text-body-sm">Access Shared</span>
                      </Tag>
                    ) : (
                      <Modal.Root>
                        <Modal.Trigger asChild>
                          <Button size="small">
                            Share access <ArrowRight color="currentColor" />
                          </Button>
                        </Modal.Trigger>
                        <GustoAccess entityId={entityId} />
                      </Modal.Root>
                    )
                  }
                />
              </Tab.Content>
              <Tab.Content
                value={ACCOUNT_CONNECTION_TYPE.AP_AR}
                className="t-overflow-y-auto t-h-[28rem] sm:t-h-auto sm:t-overflow-visible t-flex t-flex-col t-gap-3"
              >
                {hubspotConnections?.map((connection) => (
                  <BankingConnections
                    key={connection.uuid}
                    connection={connection}
                    onConnect={onConnect}
                    entityId={entityId}
                    onComplete={onComplete}
                    isOnboarding={isOnboarding}
                  />
                ))}
                <AccountCard
                  logo={<img src={BillCom} alt="Bill.com" />}
                  title="Bill.com"
                  description="Export bills and vendors directly into Inkle Books for easier bookkeeping."
                  CTA={billComConnections?.map((connection) => {
                    return connection.is_connection_available ? (
                      <Tag key={connection.uuid} tagType="green">
                        <span className="t-text-body-sm">Connected</span>
                      </Tag>
                    ) : (
                      <Modal.Root key={connection.uuid}>
                        <Modal.Trigger asChild>
                          <Button size="small">
                            Connect <ArrowRight color="currentColor" />
                          </Button>
                        </Modal.Trigger>
                        <ConnectBillDotCom
                          entityId={entityId}
                          connectionId={connection.uuid}
                        />
                      </Modal.Root>
                    );
                  })}
                />
              </Tab.Content>
            </Tab.Root>
            {bookkeepingSettings?.accounting_lock_date && (
              <div className="t-mt-4">
                <Banner
                  type="neutral"
                  title={
                    <span>
                      Your financials are locked until{" "}
                      {dayjs(bookkeepingSettings.accounting_lock_date).format(
                        "MMM DD, YYYY"
                      )}
                    </span>
                  }
                  description={
                    <span>
                      To preserve closed financials, transactions before this
                      date won't be imported. If you need to include earlier
                      transactions, either update the lock date in{" "}
                      <ConditionalLink
                        to={link("/books/books-settings")}
                        className="t-text-text-30 !t-underline"
                      >
                        settings{" "}
                      </ConditionalLink>{" "}
                      or reach out to us via{" "}
                      <ConditionalLink
                        to={`/chat/${entity?.bookkeeping_channel}`}
                        className="t-text-text-30 !t-underline"
                      >
                        chat
                      </ConditionalLink>{" "}
                      for assistance.
                    </span>
                  }
                />
              </div>
            )}
          </div>

          <Modal.Root
            open={plaidToDirectMigrationStrategyModal.isOpen}
            onOpenChange={plaidToDirectMigrationStrategyModal.close}
          >
            <Modal.Content>
              {currentConnectionId && (
                <PlaidToDirectMigration
                  connectionId={currentConnectionId}
                  onClose={plaidToDirectMigrationStrategyModal.close}
                  entityId={entityId}
                />
              )}
            </Modal.Content>
          </Modal.Root>
        </Async.Success>
      </Async.Root>
    </>
  );
};
